import React, { useState } from 'react';
import * as Sentry from '@sentry/react';
import { Button, TextButton, Typography, useGlobalContext } from 'seagull';
import ExternalLinkButton from "../components/ExternalLinkButton";
import { useStore, useStoreActions } from "../contexts";
import { getIntercomHelpArticle } from "../infra/utils";
import { BankAccountDetailsModal } from "../BankAccountDetailsModal";
import { PlaidMode, usePlaidState } from "./usePlaidState";
import { BusinessChannelEventBinder, PusherEvent } from "../pusher";
import { selectors } from "../store/state-selectors";
import { useFeatureFlags } from "./useFeatureFlags";
import ContactSupportButton from "../components/ContactSupportButton";
import { isBankAccountConnected } from "../store/store.model";
export const useConnectBankAccount = () => {
  const [plaidCanceled, setPlaidCanceled] = useState(false);
  const [isConnectingInProgress, setIsConnectingInProgress] = useState(false);
  const [showingManualBankAccountModal, setShowingManualBankAccountModal] = useState(false);
  const globalContext = useGlobalContext();
  const {
    api,
    logBI,
    fetchUserInfo
  } = useStoreActions();
  const plaidLink = usePlaidState({
    mode: {
      type: PlaidMode.Vendor
    },
    onSuccess(_account) {
      logBI(11210);
    },
    onError(_error) {
      logBI(11211);
      setPlaidCanceled(true);
      setIsConnectingInProgress(false);
    },
    onExit() {
      setPlaidCanceled(true);
      setIsConnectingInProgress(false);
    }
  });
  const onVendorBankAccountConnectedPushEvent = async _event => {
    await fetchUserInfo();
    setIsConnectingInProgress(false);
  };
  const ConnectBankAccountEventBinder = () => /*#__PURE__*/React.createElement(BusinessChannelEventBinder, {
    eventName: PusherEvent.VendorBankAccountConnected,
    callback: onVendorBankAccountConnectedPushEvent
  });
  const onDetailsSubmit = async _ref => {
    let {
      routingNumber,
      accountNumber
    } = _ref;
    logBI(11212);
    setShowingManualBankAccountModal(false);
    globalContext.startLoader();
    try {
      await api.vendor.authorizeVendorBankAccount({
        routingNumber,
        accountNumber
      });
    } catch (err) {
      Sentry.captureException(err);
      globalContext.showErrorToast('Error submitting bank account details');
      logBI(11213);
    } finally {
      globalContext.stopLoader();
    }
  };
  const onDetailsCancel = () => {
    setShowingManualBankAccountModal(false);
  };
  const Description = _ref2 => {
    let {
      testid,
      linkTestid,
      className
    } = _ref2;
    return /*#__PURE__*/React.createElement("div", {
      "data-testid": testid,
      className: className
    }, /*#__PURE__*/React.createElement(Typography, null, "Provide your bank details to receive client payments automatically. "), /*#__PURE__*/React.createElement(Typography, null, "ACH fees may apply. ", /*#__PURE__*/React.createElement(ExternalLinkButton, {
      href: getIntercomHelpArticle('5661179-payments-via-anchor'),
      testid: linkTestid,
      openInNewTab: true,
      underline: "none"
    }, "Read more")));
  };
  const ConnectButton = _ref3 => {
    let {
      className,
      testid,
      enterManuallyTestid
    } = _ref3;
    const userInfo = useStore(state => state.userInfo);
    const hasLinkedBankAccount = selectors.user.hasLinkedBankAccount({
      userInfo
    });
    const hasBankAccountConnected = selectors.user.hasBankAccountConnected({
      userInfo
    });
    const {
      EXPOSE_BANK_ACCOUNT
    } = useFeatureFlags();
    if (EXPOSE_BANK_ACCOUNT ? hasBankAccountConnected : hasLinkedBankAccount) return /*#__PURE__*/React.createElement(React.Fragment, null);
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ConnectBankAccountEventBinder, null), /*#__PURE__*/React.createElement("div", {
      className: className
    }, /*#__PURE__*/React.createElement(Button
    // mui button takes up fullwidth by default when wrapped by a flex-column container, this neutralizes that
    , {
      style: {
        width: 'fit-content'
      },
      size: "medium",
      "data-testid": testid,
      loading: isConnectingInProgress,
      onClick: () => {
        logBI(11204);
        setIsConnectingInProgress(true);
        plaidLink.open();
      }
    }, "Connect bank account"), plaidCanceled && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
      "data-testid": "".concat(enterManuallyTestid, "-description"),
      style: {
        marginTop: '12px'
      },
      variant: "paragraph2",
      skin: "secondary"
    }, "Having trouble connecting your account?"), /*#__PURE__*/React.createElement(TextButton, {
      "data-testid": "".concat(enterManuallyTestid, "-btn"),
      onClick: () => {
        logBI(11205);
        setShowingManualBankAccountModal(true);
      },
      underline: "none"
    }, "Connect using routing and account number"))), showingManualBankAccountModal && /*#__PURE__*/React.createElement(BankAccountDetailsModal, {
      open: true,
      onSubmit: onDetailsSubmit,
      onCancel: onDetailsCancel
    }));
  };
  const BankAccountDetails = _ref4 => {
    let {
      payoutBankAccount
    } = _ref4;
    return isBankAccountConnected(payoutBankAccount) ? /*#__PURE__*/React.createElement("div", {
      style: {
        display: 'flex',
        flexDirection: 'column'
      }
    }, /*#__PURE__*/React.createElement(Typography, {
      "data-testid": "connected-bank-account-details"
    }, "The connected bank account is ****".concat(payoutBankAccount.connected.last4)), /*#__PURE__*/React.createElement(Typography, {
      skin: "secondary"
    }, "For bank account changes, please ", /*#__PURE__*/React.createElement(ContactSupportButton, {
      underline: "none",
      testid: "contact-support"
    }))) : /*#__PURE__*/React.createElement(React.Fragment, null);
  };
  return {
    Description,
    ConnectButton,
    BankAccountDetails
  };
};